import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button, Chip, IconButton, Link, SvgIcon, Typography,
} from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import { makeStyles } from 'tss-react/mui';
import TooltipMUI6 from 'src/components/TooltipMUI6';
import { PERMISSIONS, STYLE_OPTIONS } from 'src/constants';
import helpers from 'src/helpers';
import {
  ACTIVITY_AREA_SETTINGS,
  fetchActivityCounts,
  markActivitySeen,
  setActivityMuted,
} from 'src/actions/activityActions';

/**
 * ActivityControls — CON-2026 (P1d). The activity badge controls shared by
 * Order Follow-Up, Events and Groups (the same look as Smart Forms'
 * "Mark all as seen" and red "N new" count):
 *   - NewActivityFilterButton: the "Unseen (N)" filter, a small chip on the
 *     list's "N found" line (a header-row control was too large).
 *   - MarkAllSeenButton: "Mark all as seen (N)", a small link beside it.
 *   - NewCountPill: the red "N new" next to an event or group's name.
 *   - NEW_ROW_SX: the red left edge and light tint on a new row.
 * All of them render nothing when there is nothing new; callers show them
 * only to ACTIVITY_BADGES holders.
 *
 * CON-2036 adds the per-item mute:
 *   - ActivityMuteBell: the bell (notifications on) or bell with a slash
 *     (muted) in a Smart Forms, Events or Groups row. One click flips it.
 *   - useActivityMute: whether the user can mute in an area, and the call
 *     the Events and Groups bulk Mute / Unmute buttons make.
 */

const useStyles = makeStyles()((theme) => ({
  newCount: {
    display: 'inline-block',
    marginLeft: 6,
    minWidth: 18,
    padding: '0 6px',
    borderRadius: 9,
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    fontSize: 11,
    fontWeight: 600,
    lineHeight: '18px',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    textTransform: 'none',
    cursor: 'pointer',
  },
}));

export const NEW_ROW_SX = {
  boxShadow: `inset 4px 0 0 ${STYLE_OPTIONS.COLOR_RED}`,
  backgroundColor: 'rgba(229, 57, 53, 0.06)',
};

export function NewActivityFilterButton({
  count = 0,
  active = false,
  onToggle = () => { },
  tooltip = '',
}) {
  if (!active && !(count > 0)) return null;

  // A small chip on the list's "N found" line, not a header control: off it
  // is red outlined; on it is filled, reads "· Show all" as the CP app's does,
  // and its x (or a click) shows all again.
  const chip = (
    <Chip
      size="small"
      color="error"
      variant={active ? 'filled' : 'outlined'}
      label={active ? `Unseen (${count}) · Show all` : `Unseen (${count})`}
      onClick={onToggle}
      onDelete={active ? onToggle : undefined}
      aria-pressed={active}
      sx={{ fontWeight: 600, height: 22 }}
    />
  );

  return tooltip ? <TooltipMUI6 title={tooltip}>{chip}</TooltipMUI6> : chip;
}

NewActivityFilterButton.propTypes = {
  count: PropTypes.number,
  active: PropTypes.bool,
  onToggle: PropTypes.func,
  tooltip: PropTypes.string,
};

export function MarkAllSeenButton({ count = 0, onClick = () => { } }) {
  if (!(count > 0)) return null;
  return (
    <Link
      component="button"
      type="button"
      variant="body2"
      underline="hover"
      onClick={onClick}
      sx={{ whiteSpace: 'nowrap' }}
    >
      {`Mark all as seen (${count})`}
    </Link>
  );
}

MarkAllSeenButton.propTypes = {
  count: PropTypes.number,
  onClick: PropTypes.func,
};

export function NewCountPill({
  count = 0, onClick = null, tooltip = 'New since you last looked.', numberOnly = false,
}) {
  const { classes } = useStyles();
  if (!(count > 0)) return null;
  return (
    <TooltipMUI6 title={tooltip}>
      <Typography
        variant="body2"
        component="span"
        className={classes.newCount}
        onClick={onClick || undefined}
      >
        {numberOnly ? `${count}` : `${count} new`}
      </Typography>
    </TooltipMUI6>
  );
}

NewCountPill.propTypes = {
  count: PropTypes.number,
  numberOnly: PropTypes.bool,
  onClick: PropTypes.func,
  tooltip: PropTypes.string,
};

/**
 * useHasActivityBadges — ACTIVITY_BADGES on the signed-in user.
 */
export function useHasActivityBadges() {
  const userRolePermissions = useSelector((state) => state?.account?.user?.userRole?.permissions);
  return helpers.permissions.doesUserHavePermission(
    PERMISSIONS.PERMISSIONS_SYSTEM_CODE.ACTIVITY_BADGES,
    userRolePermissions || [],
  );
}

/**
 * ActivityListControls — the "Unseen (N)" chip and "Mark all as seen (N)"
 * link for Order Follow-Up, Events and Groups, on the list's "N found"
 * line, from the live counts in state.activity.
 * Mark all posts the server time of the counts shown, never the browser
 * clock, so anything that arrived after them stays new.
 *
 * CON-2046: rowIds are the list's rows. With Start muted on for the area,
 * a new set of rows re-reads the settings, so an event or group created
 * since they were last read shows its bell muted.
 */
export function ActivityListControls({
  area,
  newOnly = false,
  onToggleNewOnly = () => { },
  newTooltip = '',
  rowIds = [],
}) {
  const dispatch = useDispatch();
  const hasActivityBadges = useHasActivityBadges();
  const { serverNow, total, startMuted } = useSelector((state) => ({
    serverNow: state?.activity?.serverNow,
    total: state?.activity?.areas?.[area]?.total || 0,
    startMuted: !!state?.activity?.preferences?.startMuted?.[area],
  }));
  const rowKey = (rowIds || []).join(',');
  useEffect(() => {
    if (hasActivityBadges && startMuted && rowKey) dispatch(fetchActivityCounts());
  }, [hasActivityBadges, startMuted, rowKey, dispatch]);
  if (!hasActivityBadges) return null;

  const handleMarkAll = () => {
    if (!serverNow) return;
    dispatch(markActivitySeen({ area, entityId: 0, seenThrough: serverNow }));
  };

  return (
    <>
      <NewActivityFilterButton
        count={total}
        active={newOnly}
        onToggle={onToggleNewOnly}
        tooltip={newTooltip}
      />
      <MarkAllSeenButton count={total} onClick={handleMarkAll} />
    </>
  );
}

ActivityListControls.propTypes = {
  area: PropTypes.string.isRequired,
  newOnly: PropTypes.bool,
  onToggleNewOnly: PropTypes.func,
  newTooltip: PropTypes.string,
  rowIds: PropTypes.array,
};

/**
 * ActivityRowPill — the red "N new" for one event or group, from the live
 * counts in state.activity.
 */
export function ActivityRowPill({
  area, entityId, tooltip = 'New since you last looked.', numberOnly = false,
}) {
  const hasActivityBadges = useHasActivityBadges();
  const count = useSelector((state) => state?.activity?.areas?.[area]?.byEntity?.[entityId] || 0);
  if (!hasActivityBadges) return null;
  return <NewCountPill count={count} tooltip={tooltip} numberOnly={numberOnly} />;
}

ActivityRowPill.propTypes = {
  numberOnly: PropTypes.bool,
  area: PropTypes.string.isRequired,
  entityId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  tooltip: PropTypes.string,
};

/**
 * ActivityTabLabel — CON-2036 review. A detail-page tab label with a red
 * number for that event or group beside it (the Guests and Contacts tabs),
 * so the new people are visible before the tab is opened. Number only, like
 * a form's Entries tab and the sidebar: navigation badges carry a count, the
 * list pills beside a row's other numbers carry "N new".
 */
export function ActivityTabLabel({
  label, area, entityId, tooltip = 'New since you last looked.',
}) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      {label}
      <ActivityRowPill area={area} entityId={entityId} tooltip={tooltip} numberOnly />
    </span>
  );
}

ActivityTabLabel.propTypes = {
  label: PropTypes.string.isRequired,
  area: PropTypes.string.isRequired,
  entityId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  tooltip: PropTypes.string,
};

const VISIT_NEW_SELECTORS = {
  partyGuests: (state) => ({
    total: state?.partyDetails?.partyGuestsActivityVisitTotal || 0,
    onPage: (state?.partyDetails?.partyGuestsActivityNewIds || []).length,
  }),
  groupMembers: (state) => ({
    total: state?.groupDetails?.groupMembers?.activityVisitTotal || 0,
    onPage: (state?.groupDetails?.groupMembers?.activityNewIds || []).length,
  }),
};

/**
 * ActivityVisitNewChip — CON-2036 review. "N new since your last visit" on
 * the Guests and Contacts tabs' "N found" line, the same chip as a Smart
 * Form's Entries tab. Opening the tab marks it seen, so N is what was new
 * when this visit began, and it stays for the visit like the red rows.
 */
export function ActivityVisitNewChip({ area }) {
  const hasActivityBadges = useHasActivityBadges();
  const { total, onPage } = useSelector(VISIT_NEW_SELECTORS[area] || (() => ({})));
  const count = Math.max(total || 0, onPage || 0);
  if (!hasActivityBadges || !(count > 0)) return null;
  return (
    <Chip
      size="small"
      variant="outlined"
      label={`${count} new since your last visit`}
      sx={{ color: STYLE_OPTIONS.COLOR_RED, borderColor: STYLE_OPTIONS.COLOR_RED }}
    />
  );
}

ActivityVisitNewChip.propTypes = {
  area: PropTypes.string.isRequired,
};

/**
 * activityMuteNoun — CON-2036. "form", "event" or "group" for an area's mute
 * tooltip.
 */
const activityMuteNoun = (area) => ACTIVITY_AREA_SETTINGS
  .find((row) => row.area === area)?.noun || 'item';

/**
 * useActivityMute — CON-2036. For one area: canMute (ACTIVITY_BADGES and the
 * area's own permissions, whatever the user's settings: a mute also stops
 * push), the muted ids, and setMuted(ids, muted) for the bell and the bulk
 * buttons. setMuted resolves to true when the server took it.
 */
export function useActivityMute(area) {
  const dispatch = useDispatch();
  const hasActivityBadges = useHasActivityBadges();
  const permittedAreas = useSelector((state) => state?.activity?.permittedAreas);
  const muted = useSelector((state) => state?.activity?.preferences?.muted?.[area]);
  const canMute = hasActivityBadges && (permittedAreas || []).includes(area);
  const setMuted = async (entityIds, isMuted) => {
    const ids = (entityIds || []).map(Number).filter((id) => Number.isInteger(id) && id > 0);
    if (!canMute || !ids.length) return false;
    const answer = await dispatch(setActivityMuted({ area, entityIds: ids, muted: isMuted }));
    return !!answer?.success;
  };
  return {
    canMute,
    mutedIds: muted || [],
    isMuted: (entityId) => (muted || []).includes(Number(entityId)),
    setMuted,
  };
}

/**
 * ActivityMuteBell — CON-2036. The bell in a Smart Forms, Events or Groups
 * row: a bell while notifications are on, a bell with a slash once muted.
 * One click flips it; the click never opens the row.
 */
export function ActivityMuteBell({ area, entityId }) {
  const { canMute, isMuted, setMuted } = useActivityMute(area);
  const [pending, setPending] = useState(false);
  if (!canMute) return null;
  const muted = isMuted(entityId);
  const noun = activityMuteNoun(area);
  const handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (pending) return;
    setPending(true);
    await setMuted([entityId], !muted);
    setPending(false);
  };
  return (
    <TooltipMUI6 title={muted ? `Muted: no badge or push for this ${noun}` : 'Notifications on'}>
      <IconButton
        size="small"
        onClick={handleClick}
        disabled={pending}
        aria-pressed={muted}
        aria-label={muted ? `Unmute this ${noun}` : `Mute this ${noun}`}
        data-testid={`activity-mute-${area}-${entityId}`}
      >
        {muted
          ? <NotificationsOffIcon fontSize="small" color="action" />
          : <NotificationsNoneIcon fontSize="small" color="action" />}
      </IconButton>
    </TooltipMUI6>
  );
}

ActivityMuteBell.propTypes = {
  area: PropTypes.string.isRequired,
  entityId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

/**
 * ActivityBulkMuteButtons — CON-2036. "Mute notifications" and "Unmute
 * notifications" for the Events and Groups bulk bars, with the row bell's
 * icons (bell with a slash on Mute, bell on Unmute). They act on the
 * selected rows; the selection stays, so another bulk action can follow.
 * Nothing renders for a user who cannot mute in the area.
 */
export function ActivityBulkMuteButtons({ area, selected = [], iconClassName = '' }) {
  const { canMute, setMuted } = useActivityMute(area);
  const [pending, setPending] = useState(false);
  if (!canMute) return null;
  const run = async (muted) => {
    if (pending) return;
    setPending(true);
    await setMuted(selected, muted);
    setPending(false);
  };
  return (
    <>
      <Button onClick={() => run(true)} disabled={pending || !selected.length}>
        <SvgIcon fontSize="small" className={iconClassName} component={NotificationsOffIcon} />
        MUTE NOTIFICATIONS
      </Button>
      <Button onClick={() => run(false)} disabled={pending || !selected.length}>
        <SvgIcon fontSize="small" className={iconClassName} component={NotificationsNoneIcon} />
        UNMUTE NOTIFICATIONS
      </Button>
    </>
  );
}

ActivityBulkMuteButtons.propTypes = {
  area: PropTypes.string.isRequired,
  selected: PropTypes.array,
  iconClassName: PropTypes.string,
};
